// pages/Analysis/DocumentAnalysis.tsx - Fixed with clean CSS classes
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Home, 
  List, 
  FileText, 
  Copy, 
  ChevronRight, 
  Search, 
  Download, 
  Share, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Hand, 
  Type, 
  ExternalLink,
  Plus,
  ChevronDown,
  Upload,
  FolderOpen,
  Send,
  Settings
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import PDFViewer from '../../components/pdf/PDFViewer';
import { ROUTES } from '../../constants/routes';
import './DocumentAnalysis.css';

interface Document {
  _id: string;
  filename: string;
  document_type: string;
  upload_date: string;
  processed_date: string;
  status: string;
  page_count?: number;
  ocr_data?: {
    extracted_text: string;
  };
  document_metadata?: {
    project_name?: string;
    owner?: string;
    contractor?: string;
  };
}

interface Term {
  _id: string;
  term: string;
  category: string;
  risk_level: string;
  industry_definition: string;
}

interface TermInstance {
  _id: string;
  document_id: string;
  term_id: string;
  matched_text: string;
  page_number: number;
  confidence_score: number;
  context?: string;
  highlighting_data?: {
    position_in_page: number;
    page_start_position: number;
    page_end_position: number;
    corpus_start_in_page: number;
    corpus_end_in_page: number;
    corpus_content: string;
    corpus_length: number;
    term_start_in_corpus: number;
    term_end_in_corpus: number;
    term_start_in_page: number;
    term_end_in_page: number;
    term_length: number;
    highlighting_quality?: {
      corpus_size_appropriate: boolean;
      position_accuracy: string;
      boundary_detection: string;
    };
  };
}

const DocumentAnalysis: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // All state variables properly declared
  const [document, setDocument] = useState<Document | null>(null);
  const [terms, setTerms] = useState<Term[]>([]);
  const [termInstances, setTermInstances] = useState<TermInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<TermInstance | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [highlightedInstanceIndex, setHighlightedInstanceIndex] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (documentId) {
        await fetchDocumentData();
      }
    };
    fetchData();
  }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    window.document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const quickActions = [
    {
      label: 'Upload New Contract',
      icon: Upload,
      action: () => {
        navigate(ROUTES.UPLOAD);
        setIsDropdownOpen(false);
      },
      primary: true
    },
    {
      label: 'View Dashboard',
      icon: Home,
      action: () => {
        navigate(ROUTES.DASHBOARD);
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'View All Documents',
      icon: FolderOpen,
      action: () => {
        navigate(ROUTES.DOCUMENTS);
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Export Reports',
      icon: Download,
      action: () => {
        console.log('Export reports clicked');
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Share Insights',
      icon: Send,
      action: () => {
        console.log('Share insights clicked');
        setIsDropdownOpen(false);
      }
    }
  ];

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching document data for ID:', documentId);

      // Fetch document details
      const docResponse = await fetch(`http://localhost:8000/api/documents`);
      console.log('📄 Documents response status:', docResponse.status);
      
      if (!docResponse.ok) {
        throw new Error(`Failed to fetch documents: HTTP ${docResponse.status}`);
      }
      
      const docData = await docResponse.json();
      console.log('📄 Documents data:', docData);
      
      const foundDoc = docData.data.find((doc: Document) => doc._id === documentId);
      console.log('🎯 Found document:', foundDoc);
      
      if (!foundDoc) {
        throw new Error(`Document with ID ${documentId} not found in response`);
      }
      setDocument(foundDoc);
      
      // Set total pages from document metadata if available
      if (foundDoc.page_count) {
        setTotalPages(foundDoc.page_count);
        console.log('📊 Set total pages from document:', foundDoc.page_count);
      }

      // Fetch all contract terms
      const termsResponse = await fetch('http://localhost:8000/api/contract-terms');
      console.log('🏷️ Terms response status:', termsResponse.status);
      
      if (!termsResponse.ok) {
        throw new Error(`Failed to fetch terms: HTTP ${termsResponse.status}`);
      }
      
      const termsData = await termsResponse.json();
      console.log('🏷️ Terms count:', termsData.data?.length || 0);
      setTerms(termsData.data || []);

      // Fetch term instances for this document
      const instancesResponse = await fetch('http://localhost:8000/api/term-instances');
      console.log('🔗 Instances response status:', instancesResponse.status);
      
      if (!instancesResponse.ok) {
        throw new Error(`Failed to fetch term instances: HTTP ${instancesResponse.status}`);
      }
      
      const instancesData = await instancesResponse.json();
      const documentInstances = instancesData.data.filter(
        (instance: TermInstance) => instance.document_id === documentId
      );
      console.log('🔗 Document instances count:', documentInstances.length);
      
      setTermInstances(documentInstances);

    } catch (err) {
      console.error('❌ Error fetching document data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document analysis');
    } finally {
      setLoading(false);
    }
  };

  const getTermsFoundInDocument = () => {
    const termIds = termInstances.map(instance => instance.term_id);
    let filteredTerms = terms.filter(term => termIds.includes(term._id));
    
    // Apply search filter
    if (searchTerm) {
      filteredTerms = filteredTerms.filter(term =>
        term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
        term.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filteredTerms;
  };

  const getInstancesForTerm = (termId: string) => {
    return termInstances.filter(instance => instance.term_id === termId);
  };

  // Enhanced term selection
  const handleTermClick = (term: Term) => {
    const instances = getInstancesForTerm(term._id);
    
    console.log(`🎯 Term "${term.term}" clicked:`, {
      instances: instances.length
    });
    
    setSelectedTerm(term);
    setSelectedInstance(null);
    setHighlightedInstanceIndex(0);
    
    // Immediately jump to first instance page if available
    if (instances.length > 0) {
      const firstInstance = instances[0];
      console.log(`📍 Navigating to page ${firstInstance.page_number} for first instance`);
      
      // Set the current page first
      setCurrentPage(firstInstance.page_number);
      setSelectedInstance(firstInstance);
      
      // Add a small delay to ensure the page change is processed before highlighting
      setTimeout(() => {
        console.log(`✨ Highlighting term "${term.term}" on page ${firstInstance.page_number}`);
      }, 200);
    } else {
      console.log(`⚠️ No instances found for term "${term.term}"`);
    }
  };

  const handleInstanceClick = (instance: TermInstance, index: number) => {
    console.log(`🔍 Instance ${index + 1} clicked for page ${instance.page_number}`);
    
    setSelectedInstance(instance);
    setHighlightedInstanceIndex(index);
    
    // Navigate to the instance page
    handlePageChange(instance.page_number);
    
    // Add delay for smooth navigation
    setTimeout(() => {
      console.log(`📍 Navigated to instance on page ${instance.page_number}`, selectedInstance);
    }, 100);
  };

  const navigateToNextInstance = () => {
    if (!selectedTerm) return;
    const instances = getInstancesForTerm(selectedTerm._id);
    if (instances.length === 0) return;
    
    const nextIndex = (highlightedInstanceIndex + 1) % instances.length;
    const nextInstance = instances[nextIndex];
    
    console.log(`⏭️ Moving to next instance: ${nextIndex + 1}/${instances.length} on page ${nextInstance.page_number}`);
    
    setHighlightedInstanceIndex(nextIndex);
    setSelectedInstance(nextInstance);
    handlePageChange(nextInstance.page_number);
  };

  const navigateToPrevInstance = () => {
    if (!selectedTerm) return;
    const instances = getInstancesForTerm(selectedTerm._id);
    if (instances.length === 0) return;
    
    const prevIndex = highlightedInstanceIndex === 0 ? instances.length - 1 : highlightedInstanceIndex - 1;
    const prevInstance = instances[prevIndex];
    
    console.log(`⏮️ Moving to previous instance: ${prevIndex + 1}/${instances.length} on page ${prevInstance.page_number}`);
    
    setHighlightedInstanceIndex(prevIndex);
    setSelectedInstance(prevInstance);
    handlePageChange(prevInstance.page_number);
  };

  const handlePageChange = (page: number) => {
    console.log(`📄 Page changed to: ${page}`);
    setCurrentPage(page);
  };

  const handleZoom = (direction: 'in' | 'out') => {
    if (direction === 'in' && zoomLevel < 200) {
      setZoomLevel(zoomLevel + 25);
    } else if (direction === 'out' && zoomLevel > 50) {
      setZoomLevel(zoomLevel - 25);
    }
  };

  // Handle PDF loading success to update total pages
  const handlePDFLoadSuccess = (numPages: number) => {
    setTotalPages(numPages);
  };

  if (loading) {
    return (
      <div className="analysis-loading-container">
        <LoadingSpinner size="lg" message="Loading document analysis..." />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="analysis-error-container">
        <div className="analysis-error-content">
          <h3>Error Loading Document</h3>
          <p>{error || 'Document not found'}</p>
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="error-back-button"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const documentTerms = getTermsFoundInDocument();

  return (
    <div className="analysis-container">
      {/* Top Navigation Bar */}
      <div className="analysis-top-nav">
        <div className="analysis-nav-content">
          <div className="nav-left">
            {/* Navigation Icons */}
            <div className="nav-icons">
              <button 
                onClick={() => navigate(ROUTES.DASHBOARD)}
                className="nav-icon"
                title="Dashboard"
              >
                <Home size={20} />
              </button>
              <button 
                onClick={() => navigate(ROUTES.DOCUMENTS)}
                className="nav-icon"
                title="Documents List"
              >
                <List size={20} />
              </button>
              <button 
                onClick={() => navigate(ROUTES.UPLOAD)}
                className="nav-icon"
                title="Upload Document"
              >
                <FileText size={20} />
              </button>
              <button className="nav-icon" title="Notes">
                <Copy size={20} />
              </button>
            </div>
          </div>

          {/* Centered Title */}
          <div className="nav-center">
            <div className="header-title-section">
              <h1 className="header-title">Artifi Contract Analyzer</h1>
              <p className="header-subtitle">powered by <strong>Artifi Agentic Studio</strong></p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="nav-actions">
            <button className="nav-icon" title="Settings">
              <Settings size={20} />
            </button>
            
            {/* Quick Actions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="action-button action-button-secondary flex items-center gap-2"
              >
                <Plus size={16} />
                Actions
                <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                  <div className="py-2">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={action.action}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                          action.primary ? 'text-blue-400 border-b border-gray-700' : 'text-gray-300'
                        }`}
                      >
                        <action.icon size={18} />
                        <span className="font-medium">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button className="action-button action-button-secondary">
              <Share size={16} />
              Share
            </button>
            <button className="action-button action-button-secondary">
              <Download size={16} />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Breadcrumb Section */}
      <div className="breadcrumb-section">
        <div className="breadcrumb">
          <span className="breadcrumb-item">Dashboard</span>
          <ChevronRight size={16} className="breadcrumb-separator" />
          <span className="breadcrumb-current">{document.filename}</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="analysis-main">
        {/* Left Panel - Contract Terms */}
        <div className="analysis-left-panel">
          {/* Panel Header */}
          <div className="panel-header">
            <div className="header-title">
              <div className="title-icon">
                <FileText size={14} />
              </div>
              <h2>Contract Terms</h2>
            </div>            
            {/* Search */}
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search for contract terms"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Contract Terms Section */}
          <div className="terms-section">
            <div className="terms-container">
              <h3 className="terms-heading">📊 Contract Terms</h3>
              <div className="terms-list">
                {documentTerms.map((term) => {
                  const instances = getInstancesForTerm(term._id);
                  const isSelected = selectedTerm?._id === term._id;
                  
                  return (
                    <div
                      key={term._id}
                      onClick={() => handleTermClick(term)}
                      className={`term-item ${isSelected ? 'term-item-selected' : ''}`}
                    >
                      <div className="term-content">
                        <div className="term-info">
                          <div className="term-name">{term.term}</div>
                          <div className="term-category">{term.category}</div>
                        </div>
                        <div className="term-badge">
                          <span className={`badge ${instances.length > 0 ? 'badge-active' : 'badge-inactive'}`}>
                            {instances.length}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Center Panel - Document Viewer */}
        <div className="analysis-center-panel">
          {/* Document Controls */}
          <div className="document-controls">
            <div className="controls-left">
              <div className="page-controls">
                <span className="page-label">Page</span>
                <input
                  type="number"
                  value={currentPage}
                  onChange={(e) => handlePageChange(parseInt(e.target.value))}
                  className="page-input"
                  min="1"
                  max={totalPages}
                />
                <span className="page-total">of {totalPages}</span>
              </div>
              
              <div className="zoom-controls">
                <button 
                  onClick={() => handleZoom('out')}
                  className="zoom-button"
                >
                  <ZoomOut size={16} />
                </button>
                <span className="zoom-level">{zoomLevel}%</span>
                <button 
                  onClick={() => handleZoom('in')}
                  className="zoom-button"
                >
                  <ZoomIn size={16} />
                </button>
              </div>
            </div>

            <div className="controls-right">
              <button className="tool-button">
                <Hand size={16} />
              </button>
              <button className="tool-button">
                <Type size={16} />
              </button>
              <button className="tool-button tool-button-highlight">
                🖍️
              </button>
              <button className="tool-button">
                <RotateCw size={16} />
              </button>
              <button className="tool-button">
                <ExternalLink size={16} />
              </button>
            </div>
          </div>

          {/* Clean Document Viewer */}
          <div className="document-viewer">
            <div className="document-page">
              {/* Clean Document Header - Only filename and page */}
              <div className="document-page-header">
                <div className="page-info">
                  <span className="filename">{document.filename}</span>
                  <span className="page-number">Page {currentPage} of {totalPages}</span>
                </div>
              </div>

              {/* Clean PDF Content */}
              <div className="pdf-container">
                {documentId && (
                  <PDFViewer
                    documentId={documentId}
                    selectedTerm={selectedTerm}
                    termInstances={termInstances}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    zoomLevel={zoomLevel}
                    highlightedInstanceIndex={highlightedInstanceIndex}
                    onLoadSuccess={handlePDFLoadSuccess}
                  />
                )}
                
                {!documentId && (
                  <div className="pdf-error">
                    <p>No document ID provided</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Clean Insights */}
        <div className="analysis-right-panel">
          {selectedTerm ? (
            <>
              {/* Clean Term Details Header */}
              <div className="insights-header">
                <div style={{ marginBottom: '8px' }}>
                  <h3>{selectedTerm.term}</h3>
                </div>
                <p className="insights-subtitle">In contract references</p>
                
                <div className="term-definition">
                  <h4>Definition</h4>
                  <p>{selectedTerm.industry_definition}</p>
                  <div className="risk-indicator">
                    <span className={`risk-badge risk-${selectedTerm.risk_level}`}>
                      {selectedTerm.risk_level.toUpperCase()} Risk
                    </span>
                  </div>
                </div>
              </div>

              {/* Clean Term Occurrences */}
              <div className="insights-content">
                <div className="insights-header-controls">
                  <h4>Insights</h4>
                  {selectedTerm && getInstancesForTerm(selectedTerm._id).length > 1 && (
                    <div className="term-navigation-compact">
                      <button 
                        onClick={navigateToPrevInstance}
                        className="nav-compact-button"
                        title="Previous Occurrence"
                      >
                        ←
                      </button>
                      <span className="nav-compact-counter">
                        {highlightedInstanceIndex + 1}/{getInstancesForTerm(selectedTerm._id).length}
                      </span>
                      <button 
                        onClick={navigateToNextInstance}
                        className="nav-compact-button"
                        title="Next Occurrence"
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="occurrences-list">
                  {getInstancesForTerm(selectedTerm._id).map((instance, index) => (
                    <div 
                      key={instance._id}
                      className={`occurrence-item ${highlightedInstanceIndex === index ? 'occurrence-highlighted' : ''}`}
                      onClick={() => handleInstanceClick(instance, index)}
                    >
                      <div className="occurrence-header">
                        <div className="occurrence-indicator">
                          <span className="indicator-badge">
                            {index + 1}
                          </span>
                        </div>
                        <span className="occurrence-page">
                          Page {instance.page_number}
                        </span>
                        <span className="occurrence-location">
                          {highlightedInstanceIndex === index ? '● Active' : 'Go to →'}
                        </span>
                      </div>
                      
                      <p className="occurrence-text">
                        "{instance.matched_text}"
                      </p>
                      
                      {/* Clean context display */}
                      {instance.highlighting_data?.corpus_content ? (
                        <p className="occurrence-context" style={{ fontStyle: 'italic', color: '#666' }}>
                          {instance.highlighting_data.corpus_content.substring(0, 100)}...
                        </p>
                      ) : instance.context ? (
                        <p className="occurrence-context">
                          {instance.context}
                        </p>
                      ) : null}
                      
                      <div className="occurrence-confidence">
                        <span>Confidence: {Math.round(instance.confidence_score * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Clean Navigation Controls */}
                {getInstancesForTerm(selectedTerm._id).length > 1 && (
                  <div className="instance-navigation-enhanced">
                    <div className="navigation-info">
                      <span className="nav-info-text">
                        Navigate through {getInstancesForTerm(selectedTerm._id).length} occurrences
                      </span>
                    </div>
                    <div className="navigation-buttons">
                      <button 
                        onClick={navigateToPrevInstance}
                        className="nav-instance-button"
                        title="Previous Instance"
                      >
                        ← Previous
                      </button>
                      <div className="instance-indicator">
                        <span className="instance-counter">
                          {highlightedInstanceIndex + 1} of {getInstancesForTerm(selectedTerm._id).length}
                        </span>
                        <div className="instance-dots">
                          {getInstancesForTerm(selectedTerm._id).map((instance, index) => (
                            <span 
                              key={index}
                              className={`instance-dot ${index === highlightedInstanceIndex ? 'active' : ''}`}
                              onClick={() => handleInstanceClick(getInstancesForTerm(selectedTerm._id)[index], index)}
                              title={`Instance ${index + 1} - Page ${instance.page_number}`}
                              style={{
                                backgroundColor: index === highlightedInstanceIndex ? '#007bff' : '#ccc'
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <button 
                        onClick={navigateToNextInstance}
                        className="nav-instance-button"
                        title="Next Instance"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="chat-section">
                <button className="chat-button">
                  💬 Chat with your contract
                </button>
              </div>
            </>
          ) : (
            <div className="insights-empty">
              <FileText size={48} />
              <h3>Select a Term</h3>
              <p>
                Click on a contract term from the left panel to view its details and occurrences in the contract.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysis;